// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import Modal from '../../Modals/Modal';

import '../../../styles/forms.css'; 
import '../../../styles/input.css'; 
import '../../../styles/classification-rules.css'; 
import '../../../styles/investments-filters.css';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import DismissContainer from '../../DismissContainer';
import { Calendar } from 'react-date-range';
import { operators, evaluateCondition, rulePasses } from '../../Investments/helpers/classificationRules';
import txHelpers from '../../Investments/helpers/index';
import { toast } from 'react-toastify';

const STANDARD_YEAR = 2024; // Leap year to accommodate Feb 29 

export default class ClassificationRule extends Modal {
    state = {
        isEditing: null, 
        editing: {}, // Rule... 
        rules: [], 
        holdProgress: 0,
        showDatePicker: false, 
    }

    componentDidMount = () => {
        this.setState({rules: this.props.data?.rules || []});
    }

    componentDidUpdate(prevProps, prevState) {
        if(this.state.isEditing === prevState.isEditing)
            return;  

        const element = document.getElementById(`rule-${this.state.isEditing}`); 
        if(!element) return;  

        element.scrollIntoView({behavior: 'smooth', block: 'center'});
    }

    renderRules = () => {
        return (
            <div> 
                <div className="Rules">
                    {this.state.rules.length === 0 && 
                        <p>No classification rules have been created for this plan or account yet...</p>
                    }
                    {this.state.rules.map((rule, index) => {
                        if(index === this.state.isEditing) 
                            return this.renderEditing() 


                        let colorIndex = Object.keys(txHelpers.transactionTypeMapping).findIndex(t => t.toLowerCase().includes(rule.txType?.toLowerCase()));
                        colorIndex = colorIndex % 8; 

                        return (
                            <div 
                                className="rule"
                                id={`rule-${index}`}
                                data-type={rule.txType || 'other'}
                                style={{
                                    backgroundImage: `url(/icons/txTypes/${rule.txType ? rule.txType.replace(/ /g, '_').toLowerCase() : 'other'}.png)`,
                                }}
                            >
                                <div className="rule-name">
                                    {rule.name}
                                    <span onClick={() => this.setState({isEditing: index, editing: {...rule}})}>Edit</span>
                                </div>
                                <div className="rule-conditions">
                                    {rule.when?.map((condition, idx) => {
                                        const field = condition.field.charAt(0).toUpperCase() + condition.field.slice(1); 
                                        const op = condition?.op?.replace(/_/g, ' '); 
                                        let value = condition.value;  
                                        if(condition.field === 'date' && condition.value){
                                            const dateObj = new Date(`${condition.value}-${STANDARD_YEAR}`); 
                                            value = dateObj.toLocaleDateString('en-US', {
                                                month: 'long',
                                                day: '2-digit',
                                                timeZone: 'UTC'
                                            });
                                        } 
                                        return <div key={idx} className="rule-condition rule-tag">
                                            • {idx > 0 ? (rule.match === 'all' ? 'and ' : 'or ') : ''}
                                            {field} {op} {value}
                                        </div>
                                    })}
                                </div>
                                <div style={{display: 'flex', gap: '20px'}}>
                                    <div 
                                        className="rule-type rule-tag"
                                        style={{
                                            color: `var(--palette-color-${colorIndex})`
                                        }}
                                    >{rule.txType || '-'} • {rule.then?.find(t => t.field === 'subtype')?.value || '-'}</div>
                                    <div className="rule-scope rule-tag">For this {rule.scope === 'plan' ? 'Plan' : 'Account'}</div>
                                </div>
                                

                            </div>
                        )
                    })}
                   
                </div>      
                 <div 
                        className="add-rule"
                        onClick={() => {
                            if(this.state.isEditing !== null) return toast.error('Please finish editing the current rule before adding a new one.'); 
                            this.setState({
                                isEditing: this.state.rules.length, 
                                editing: {
                                    name: '', 
                                    txType: null,
                                    match: 'all', 
                                    scope: 'account', 
                                    referenceId: this.props.data?.account?._id || null 
                                }, 
                                rules: [...this.state.rules, {}]
                            })}
                        }
                    >
                        ＋ Add a new classification rule
                    </div>            
            </div>
        )
    }

    renderEditing = () => {
        return (
            <div className="config-form editing-rule" id={`rule-${this.state.isEditing}`}>
                <div className="editing-section details">
                    <div>
                        <label>Rule Name</label>
                        <input 
                            id='edit-rule-name'
                            name='edit rule name'
                            type='text'
                            placeholder='Give your rule a name...'
                            value={this.state.editing.name || ''}
                            onChange={(e) => {
                                this.setState({editing: {
                                    ...this.state.editing,
                                    name: e.target.value,
                                }});
                            }}
                        />
                    </div>
                    
                    <div>
                        <label>Transaction Type</label>
                        <Select 
                            className="editing-section-select-container"
                            classNamePrefix="filter-select"
                            options={[
                                {value: "Deposit", label: "Deposit"},
                                {value: "Fees", label: "Fees"},
                                {value: "Income", label: "Income"},
                                {value: "Withdrawal", label: "Withdrawal"},
                                {value: "Rollover", label: "Rollover"},
                                {value: "Reinvestment", label: "Reinvestment"},
                                {value: "Loan Payment", label: "Loan Payment"},
                                {value: "Transfer", label: "Transfer"},
                                {value: "Receivable", label: "Receivable"},
                                {value: "Other transaction", label: "Other transaction"},
                                {value: "Other", label: "-"},
                            ]}
                            onChange={(selectedOption) => {
                                this.setState({editing: {
                                    ...this.state.editing,
                                    txType: selectedOption?.value,
                                }});
                            }}
                            value={
                                this.state.editing.txType
                                    ? {value: this.state.editing.txType, label: this.state.editing.txType}
                                    : null
                            }
                        />
                    </div>
                   
                </div>
                <div className="editing-section">
                    <div className="editing-section-title">
                        When<strong> {this.state.editing.match === 'all'? "ALL": "ANY"} </strong>of the folowing are true
                        <div className='scope'>
                            <div 
                                data-selected={this.state.editing.match==="all"}
                                onClick={() => this.setState({editing: {
                                    ...this.state.editing, 
                                    match: "all"
                                }})}
                            >All</div>
                            <div 
                                data-selected={this.state.editing.match==="any"}
                                onClick={() => this.setState({editing: {
                                    ...this.state.editing, 
                                    match: "any"
                                }})}
                            >Any</div>
                        </div>
                    </div>
                    <div className="editing-section-border" />
                    {this.state.editing.when?.map(this.renderCondition)}
                    <div 
                        className="editing-section-add-condition"
                        onClick={() => {
                            this.setState({editing: {
                                ...this.state.editing,
                                when: [
                                    ...(this.state.editing.when || []),
                                    {
                                        field: 'description',
                                        op: null,
                                        value: '',
                                    }
                                ]
                            }});
                        }}
                    >
                        ＋ Add condition
                    </div>
                </div>
                <div className="editing-section">
                    <div className="editing-section-title">
                        Then classify as...
                    </div>
                    <CreatableSelect 
                        className="editing-section-select-container"
                        classNamePrefix="filter-select"
                        isMulti={false}
                        isClearable={false}
                        autoBlur={false}
                        maxMenuHeight={100}
                        options={(this.props.data?.subTypes[this.state.editing?.txType] || []).map(subType => ({
                            label: subType,
                            value: subType,
                        }))}
                        onChange={(selectedOption) => {
                            const then = []; 
                            if(selectedOption){
                                then.push({
                                    field: 'subtype', 
                                    value: selectedOption.value
                                })
                            }
                            this.setState({editing: {
                                ...this.state.editing,
                                then
                            }});
                        }}
                        value={this.state.editing.then?.length > 0 ? {
                            label: this.state.editing.then[0].value,
                            value: this.state.editing.then[0].value,
                        } : null}
                    />
                </div>
                <div className="editing-section">
                    <div className="editing-section-title">
                        For this<strong> {this.state.editing.scope === 'account'? "ACCOUNT": "PLAN"} </strong>
                        <div className='scope'>
                            <div 
                                data-selected={this.state.editing.scope==="account"}
                                onClick={() => this.setState({editing: {
                                    ...this.state.editing, 
                                    scope: "account",
                                    referenceId: this.props.data?.account?._id || null, 
                                }})}
                            >Account</div>
                            <div
                                data-selected={this.state.editing.scope==="plan"}
                                onClick={() => this.setState({editing: {
                                    ...this.state.editing, 
                                    scope: "plan", 
                                    referenceId: this.props.data?.plan?._id || null, 
                                }})}
                            >Plan</div>
                        </div>
                    </div>
                </div>
                <div className="editing-section">
                    <div className="editing-section-actions">
                        <div 
                            className={`action-button remove${this.state.holdProgress ? ' hold-progress' : ''}`}
                            style={{
                                background: this.state.holdProgress
                                    ? `linear-gradient(to right, #ff6666 ${this.state.holdProgress * 100}%, var(--color-background-raised) 0%)`
                                    : undefined,
                                transition: 'background 0.2s linear'
                            }}
                            onMouseDown={() => {
                                let start = Date.now();
                                this.setState({holdProgress: 0});
                                this.holdInterval = setInterval(() => {
                                    const elapsed = Date.now() - start;
                                    let progress = Math.min(elapsed / 1200, 1);
                                    this.setState({holdProgress: progress});
                                    if (progress === 1) {
                                        clearInterval(this.holdInterval);
                                    }
                                }, 16);
                                this.deleteTimeout = setTimeout(() => {
                                    const rules = [...this.state.rules];
                                    this.props.data.removeRule(rules[this.state.isEditing]);
                                    rules.splice(this.state.isEditing, 1);
                                    this.setState({rules, isEditing: null, editing: {}, holdProgress: 0});
                                }, 1200); // Hold for 1.2 seconds
                            }}
                            onMouseUp={() => {
                                clearTimeout(this.deleteTimeout);
                                clearInterval(this.holdInterval);
                                this.setState({holdProgress: 0});
                            }}
                            onMouseLeave={() => {
                                clearTimeout(this.deleteTimeout);
                                clearInterval(this.holdInterval);
                                this.setState({holdProgress: 0});
                            }}
                        >Hold to Remove</div>
                        <div 
                            className='action-button cancel'
                            onClick={() => {
                                let rules = [...this.state.rules]; 
                                if(!rules[this.state.isEditing]?._id){
                                    rules.splice(this.state.isEditing, 1);
                                }

                                this.setState({isEditing: null, editing: {}, rules});
                            }}
                        >Cancel</div>
                        <div 
                            className='action-button save'
                            onClick={() => {
                                if(!this.state.editing.txType || !this.state.editing.name || !this.state.editing.then?.length || !this.state.editing.when?.length){
                                    toast.error('Please complete all required fields before saving the rule.'); 
                                    return; 
                                }

                                let rules = [...this.state.rules];
                                rules[this.state.isEditing] = this.state.editing;

                                this.props.data.saveRule(this.state.editing); 
                                this.setState({rules, isEditing: null, editing: {}});
                            }}
                        >Save</div>    
                    </div>
                </div>
            </div>
        )
    }

    // Helper function to render editing conditions...  
    renderCondition = (condition, index) => {
        const value = () => {
            if(!condition?.field) return ''; 
            if(condition?.field !== 'date')
                return condition?.value || ''; 
            
            // Format date value... MM-DD 
            let value = condition?.value || '';  
            const dateObj = new Date(`${value || '01-01'}-${STANDARD_YEAR}`); 
            return dateObj.toLocaleDateString('en-US', {
                month: 'long',
                day: '2-digit',
                timeZone: 'UTC'
            }); 
        }

        return (
            <div className="editing-section-condition" key={index}>
                <Select 
                    className="editing-section-select-container"
                    classNamePrefix="filter-select" 
                    options={Object.keys(operators).map(key => ({
                        label: key.charAt(0).toUpperCase() + key.slice(1),
                        value: key,
                    }))}
                    onChange={(selectedOption) => {
                        const conditions = [...(this.state.editing.when || [])];
                        conditions[index] = {
                            field: selectedOption?.value,
                        };
                        this.setState({editing: {
                            ...this.state.editing,
                            when: conditions,
                        }});
                    }}
                    value={{label: condition?.field ? condition.field.charAt(0).toUpperCase() + condition.field.slice(1) : '', value: condition?.field}}
                />
                <Select 
                    className="editing-section-select-container"
                    classNamePrefix="filter-select"     
                    options={operators[condition?.field] || []}
                    onChange={(selectedOption) => {
                        const conditions = [...(this.state.editing.when || [])];
                        conditions[index] = {
                            ...conditions[index],
                            op: selectedOption?.value,
                        };
                        this.setState({editing: {
                            ...this.state.editing,
                            when: conditions,
                        }});
                    }}
                    value={operators[condition?.field]?.find(option => option.value === condition?.op) || null}
                />
                {
                    <DismissContainer 
                        onClose={() => {
                            if(this.state.showDatePicker !== index) return;
                            console.log('closing date picker', index);
                            this.setState({showDatePicker: false}); 
                        }}
                        id={`date-picker-container-${index}`}
                        noAbsolute={true}
                        noZIndex={true}
                    >
                        <input 
                            type='text'
                            value={value()}
                            disabled={!condition?.op}
                            placeholder='Enter value...'
                            onFocus={(e) => {
                                e.preventDefault();
                                e.stopPropagation(); 
                                this.setState({showDatePicker: condition?.field === 'date'? index : false}); 
                            }}
                            onChange={(e) => {
                                if(condition?.field === 'date')
                                    return; // No typing for for date

                                let value = e.target.value; 
                                if(condition?.field === 'amount'){
                                    value = value.replace(/[^0-9.,-]/g, '');
                                }
                                const conditions = [...(this.state.editing.when || [])];
                                conditions[index] = {
                                    ...conditions[index],
                                    value,
                                };
                                this.setState({editing: {
                                    ...this.state.editing,
                                    when: conditions,
                                }});
                            }}
                            onBlur={() => {
                                if(condition?.field === 'date')
                                    return; // No typing for for date

                                if(condition?.field !== 'amount')
                                    return;

                                let value = parseFloat(condition?.value?.replace(/[^0-9.-]/g, ''));
                                if(isNaN(value)) value = 0;
                                const conditions = [...(this.state.editing.when || [])];
                                conditions[index] = {
                                    ...conditions[index],
                                    value,
                                };
                                this.setState({editing: {
                                    ...this.state.editing,
                                    when: conditions,
                                }})
                            }}
                        />

                        {condition?.field === 'date' && condition?.op && this.state.showDatePicker === index && 
                                <div
                                    style={{
                                        marginTop: -10,
                                        marginLeft: -70,
                                        boxShadow: '2px 3px 20px -5px rgba(0,0,0,0.12)',
                                        position: 'absolute',
                                        zIndex: 1, 
                                    }}
                                >
                                    <Calendar 
                                        className="date-picker hide-year"
                                        showMonthAndYearPickers={true}
                                        dateDisplayFormat="MMMM dd"
                                        date={new Date(`${condition?.value || '01-01'}-${STANDARD_YEAR}`)}
                                        months={1}
                                        direction="horizontal"
                                        editableDateInputs={true}
                                        onChange={(value) => {
                                            const dateObj = value instanceof Date ? value : new Date(value);
                                            const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
                                            const day = dateObj.getDate().toString().padStart(2, '0'); 
                                            const conditions = [...(this.state.editing.when || [])]; 

                                            conditions[index] = {
                                                ...conditions[index],
                                                value: `${month}-${day}`,
                                            };
                                            this.setState({editing: {
                                                ...this.state.editing,
                                                conditions,
                                            }});
                                        }}
                                    />
                                </div>
                        }
                    </DismissContainer>
                }
                <div 
                    className="editing-section-condition-remove"
                    onClick={() => {
                        const conditions = [...(this.state.editing.when || [])];
                        conditions.splice(index, 1);
                        this.setState({editing: {
                            ...this.state.editing,
                            when: conditions,
                        }});
                    }}
                />
            </div>
        )
    }

    renderTransactionPreview = () => {
        const transactions = (this.props.data?.transactions?.filter(tx => tx.txType === this.state.editing.txType) || []);
        return (
            <div className="classification-rules-transaction-preview-container">
                {!transactions.length && 
                    <div className="no-tx-preview">
                        No transactions available for preview.
                    </div>
                }
                {transactions?.map((tx, i) => {
                    const formattedDate = tx.date ? new Date(tx.date)?.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        timeZone: 'UTC'
                    }) : '-' || '-';

                    const formattedAmount = tx.amount < 0 ? 
                        `(${(tx.amount * -1).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`: 
                        tx.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                    
                    const passes = rulePasses(this.state.editing, tx); 
                    
                    return (
                        <div className="classification-rules-tx-preview">
                            <div className="tx-preview-type">{tx.txType}</div>
                            <div className="tx-preview-date">{formattedDate}</div>
                            <div className="tx-preview-description">{tx.description}</div>
                            <div className="tx-preview-amount">${formattedAmount}</div>
                            <div className="tx-preview-passes" data-passes={passes}>
                                {passes ? "Affected by current rule" : "Unaffected by current rule"}
                                {passes ? 
                                    null
                                : 
                                    this.state.editing.when?.map((condition, idx) => {
                                        if(!condition?.field || !condition?.op || condition?.value === undefined || condition?.value === null)
                                            return null;
                                        
                                        const fieldValue = tx[condition.field]; 
                                        const conditionMet = evaluateCondition(condition, fieldValue);
                                        const conditionText = () => {
                                            const field = condition.field.charAt(0).toUpperCase() + condition.field.slice(1); 
                                            const op = condition?.op?.replace(/_/g, ' '); 
                                            let value = condition.value;  
                                            if(condition.field === 'date' && condition.value){
                                                const dateObj = new Date(`${condition.value}-${STANDARD_YEAR}`); 
                                                value = dateObj.toLocaleDateString('en-US', {
                                                    month: 'long',
                                                    day: '2-digit',
                                                    timeZone: 'UTC'
                                                });
                                            }

                                            let text = `• ${field} ${op} ${value}`; 
                                            return text; 
                                        }
                                        return <div key={idx} data-passes={conditionMet}>
                                            {conditionText()}
                                        </div>;
                                    })
                                }
                            </div>
                        </div>
                    )
                })}
            </div>
        ); 
    }

    renderContent = () => {
        const showPreview = this.state.isEditing >= 0 && this.state.editing.when?.length && this.state.editing.txType; 
        return (
            <div
                className={`modal-content modal-center ${showPreview ? 'modal-shifted': ''}`}
            >
                <h1>Subtype Classification Rules</h1>
                <p>
                    Create rules to automatically classify subtypes for transactions. 
                </p>
                <p>
                    Rules are applied in order from top to bottom. You can edit, remove, or add new rules as needed.
                    The first rule that matches a transaction will determine its subtype classification.
                </p>

                {this.renderRules()}
                {
                    showPreview && 
                    this.renderTransactionPreview()
                }

                <div
                    className="button"
                    style={{
                        float: 'none',
                        backgroundImage: "url('https://static.stax.ai/images/icons/reviewed.png')",
                        marginTop: 16
                    }}
                    onClick={() => {
                         this.props.data.onClose(this.state.rules);  
                    }}
                >
                    Save & Close
                </div>
            </div>
        );
    }
};